import { FC, useEffect, useRef, useState } from "react";
import { useAppStateContext } from "../../../middleware/context/state-context-provider";
import { PdfHandler } from "../../../middleware/handlers/pdf-handler";
import type { PdfViewerState } from "../../../core/pdf/pdf-scene";

interface PdfViewportProps {
  onViewerChange: (viewerState: Partial<PdfViewerState>) => void;
  // a message from the viewer around it, such as print being prepared
  pdfStatus: string | null;
  // over each half while comparing side by side: this sheet, then the other
  compareLabels: [string, string] | null;
}

export const PdfViewport: FC<PdfViewportProps> = ({
  onViewerChange,
  pdfStatus,
  compareLabels,
}) => {
  const [state, dispatch] = useAppStateContext();
  const viewerContainerRef = useRef<HTMLDivElement>(null);
  const compareContainerRef = useRef<HTMLDivElement>(null);
  // null while nothing is loading; a percentage, or "" when the size is unknown
  const [loadProgress, setLoadProgress] = useState<number | "" | null>(null);

  useEffect(() => {
    const viewerContainer = viewerContainerRef.current;
    const compareContainer = compareContainerRef.current;
    if (!viewerContainer || !compareContainer || !state.user) return;

    PdfHandler.Start([viewerContainer, compareContainer, onViewerChange]);

    return () => {
      PdfHandler.Close();
      dispatch({ type: "CLOSE_PDF" });
    };
  }, []);

  useEffect(() => {
    // A big drawing set can take a while to arrive. If the user opens another
    // PDF meanwhile, this one is stale and must not land over it.
    let abandoned = false;
    // Read in ranges, the document opens before the whole file has arrived
    // and progress keeps being reported after that; none of it may bring the
    // label back.
    let opened = false;

    if (state.openPdf && "pdfFileUrl" in state.openPdf) {
      setLoadProgress("");

      PdfHandler.Open(state.openPdf["pdfFileUrl"], (percent) => {
        if (!abandoned && !opened) {
          setLoadProgress(percent ?? "");
        }
      })
        .then(() => {
          opened = true;
          if (!abandoned) {
            setLoadProgress(null);
          }
        })
        .catch((error: any) => {
          if (abandoned) {
            return;
          }
          console.error("Error opening PDF:", error);
          setLoadProgress(null);
          dispatch({
            type: "RESPONSE",
            response:
              error?.name === "PasswordException"
                ? "This PDF is password protected and cannot be opened in the viewer. Download it to open it."
                : "Failed to open the PDF. Please try again.",
          });
        });
    }

    return () => {
      abandoned = true;
    };
  }, [state.openPdf]);

  return (
    <div id="pdfViewport">
      <div id="pdfViewerContainer" ref={viewerContainerRef}>
        <div className="pdfViewer"></div>
      </div>
      <div id="pdfCompareContainer" ref={compareContainerRef}>
        <div className="pdfViewer"></div>
      </div>
      {compareLabels ? (
        <>
          <span className="pdfCompareLabel">{compareLabels[0]}</span>
          <span className="pdfCompareLabel pdfCompareLabelOther">
            {compareLabels[1]}
          </span>
        </>
      ) : (
        <></>
      )}
      {/* The overlay's colours: each sheet's ink goes to its own colour by
          its lightness, and paper stays white. */}
      <svg className="pdfCompareFilters" aria-hidden="true">
        <filter id="pdfCompareRed" colorInterpolationFilters="sRGB">
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 1  0.2126 0.7152 0.0722 0 0  0.2126 0.7152 0.0722 0 0  0 0 0 1 0"
          />
        </filter>
        <filter id="pdfCompareBlue" colorInterpolationFilters="sRGB">
          <feColorMatrix
            type="matrix"
            values="0.2126 0.7152 0.0722 0 0  0.2126 0.7152 0.0722 0 0  0 0 0 0 1  0 0 0 1 0"
          />
        </filter>
      </svg>
      {loadProgress !== null ? (
        <span className="pdfViewportStatus">
          {loadProgress === ""
            ? "Opening PDF..."
            : `Opening PDF... ${loadProgress}%`}
        </span>
      ) : pdfStatus !== null ? (
        <span className="pdfViewportStatus">{pdfStatus}</span>
      ) : (
        <></>
      )}
    </div>
  );
};
